import React, { useEffect, useState } from "react"

import { toggle } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"

import { useInstallationReferenceFilter } from "entities/building-references/model/useInstallationReferenceFilter"
import { InstallationReferenceFilterState } from "entities/building-references/types/InstallationReferenceFilterState"
import { useInstallationReferenceFilterViewModel } from "widgets/installation-reference-selection/installation-reference-filter/hooks/useInstallationReferenceFilterViewModel"

import { InstallationReferenceSelectionModel } from "../InstallationReferenceSelection"
import { InstallationReferenceSelectionViewModel } from "../InstallationReferenceSelectionView"

export function useInstallationReferenceSelectionViewModel({
  installationReferences,
  availableProductFilterOptions,
  defaultFilters,
  defaultSelectedInstallationReferenceIDs,
  formId,
  onSubmit,
  onZoomImage
}: InstallationReferenceSelectionModel): InstallationReferenceSelectionViewModel {
  const [formData, setFormData] = useState<
    InstallationReferenceFilterState | undefined
  >(defaultFilters)

  const installationReferenceFilterViewModel =
    useInstallationReferenceFilterViewModel({
      availableProductFilterOptions,
      defaultValues: formData
    })

  const { form } = installationReferenceFilterViewModel
  const { watch, reset } = form

  useEffect(() => {
    const subscription = watch(data =>
      setFormData(data as InstallationReferenceFilterState)
    )
    return () => subscription.unsubscribe()
  }, [watch])

  const onFilterChange = useEvent((state: InstallationReferenceFilterState) => {
    reset(state)
  })

  const availableInstallationReferences = useInstallationReferenceFilter({
    installationReferences,
    filters: formData
  })

  const [
    selectedInstallationReferenceIDs,
    setSelectedInstallationReferenceIDs
  ] = useState<string[]>(defaultSelectedInstallationReferenceIDs || [])

  const onSelect = useEvent((id: string) => {
    setSelectedInstallationReferenceIDs(prev => toggle(id, prev))
  })

  const handleSubmit = useEvent((ev: React.FormEvent) => {
    ev.preventDefault()
    onSubmit(selectedInstallationReferenceIDs)
  })

  return {
    installationReferenceFilterViewModel,
    filterState: formData || {},
    onFilterChange,
    availableInstallationReferences,
    selectedInstallationReferenceIDs,
    onSelect,
    onSubmit: handleSubmit,
    onZoomImage,
    formId
  }
}
